// src/pages/ContactSalesPage.jsx
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faUsers, faEnvelope, faPhone, faPaperPlane, faInfoCircle,
    faUser, faConciergeBell, faAlignLeft, faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { FaLinkedin, FaTwitter, FaInstagram } from 'react-icons/fa'; // Manteniendo si las usas para info de contacto
import Turnstile from 'react-turnstile';
import Header from '../components/Header';
import Footer from '../components/Footer';
// Asegúrate de que los estilos de ContactPage.css (si los tienes para floating labels) estén disponibles
// o que estés usando Bootstrap 5+ que soporta floating labels nativamente con su estructura.
// import './ContactPage.css'; // Si tienes estilos específicos para floating-label-group etc.

const ContactSalesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const appName = "MyFit Menu AI"; // Para el email de contacto

    const [formData, setFormData] = useState({
        fullName: '',
        workEmail: '',
        companyName: '',
        teamSize: '', // Nuevo campo
        phone: '',
        message: '', // Renombrado de 'subject' y 'message' a solo 'message' para ventas
    });
    const [planOfInterest, setPlanOfInterest] = useState('');
    const [captchaToken, setCaptchaToken] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: null, text: '' });

    const salesWorkerUrl = 'https://myfitmenu-ai.com/api/contact'; // URL para tu endpoint de ventas

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const plan = queryParams.get('plan');
        if (plan) {
            setPlanOfInterest(plan.charAt(0).toUpperCase() + plan.slice(1));
            // Podrías pre-rellenar el mensaje si el plan es "Business"
            if (plan.toLowerCase() === 'business') {
                setFormData(prev => ({ ...prev, message: `Hello, I'm interested in learning more about the Business Plan for MyFit Menu AI.\n\nMy company is...\nOur needs are...\n` }));
            }
        }
    }, [location]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!captchaToken) {
            setSubmitStatus({ type: 'danger', text: 'Please complete the security check.' });
            return;
        }
        setIsSubmitting(true);
        setSubmitStatus({ type: null, text: '' });

        try {
            const response = await fetch(salesWorkerUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                // Incluye el plan de interés en el cuerpo si tu backend lo espera
                body: JSON.stringify({ ...formData, "cf-turnstile-response": captchaToken, planOfInterest }),
            });
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || result.details || `Server responded with ${response.status}`);
            }

            setSubmitStatus({
                type: 'success',
                text: result.message || "Thank you for your inquiry! Our sales team will contact you shortly."
            });
            setFormData({ fullName: '', workEmail: '', companyName: '', teamSize: '', phone: '', message: '' });
            setCaptchaToken(''); // Resetear el token de captcha para que se necesite uno nuevo
        } catch (error) {
            setSubmitStatus({
                type: 'danger',
                text: error.message || 'There was an error submitting your request. Please try again or contact us directly.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Para asegurar que la clave del sitio de Turnstile se carga (si la necesitas también aquí)
    // console.log("Turnstile Site Key for Sales:", process.env.REACT_APP_TURNSTILE_SITE_KEY);

    return (
        <div className="min-vh-100 d-flex flex-column"> {/* Removido contact-page-wrapper si no es global */}
            <Header />
            <main className="flex-grow-1 py-5">
                <Container>
                    <Row className="text-center mb-5">
                        <Col>
                            <FontAwesomeIcon icon={faConciergeBell} size="3x" className="text-primary mb-3" />
                            <h1 className="display-4 fw-bolder">Contact Sales</h1>
                            <p className="lead text-muted fs-5 px-md-5">
                                We're excited to discuss how MyFit Menu AI can benefit your business.
                                Please fill out the form below, and our team will be in touch.
                            </p>
                        </Col>
                    </Row>

                    {planOfInterest && (
                        <Row className="justify-content-center mb-4">
                            <Col md={10} lg={8}>
                                <Alert variant="info">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    You're inquiring about our <strong>{planOfInterest} Plan</strong>.
                                    We'll tailor our conversation to your specific needs.
                                </Alert>
                            </Col>
                        </Row>
                    )}


                    <Row className="g-5 align-items-stretch">
                        <Col lg={7} className="mb-4 mb-lg-0 d-flex">
                            <Card className="contact-form-card shadow-lg border-0 w-100">
                                <Card.Body className="p-4 p-md-5">
                                    <h3 className="fw-bold mb-4 text-primary">Tell Us About Your Business</h3>
                                    {submitStatus.text && (
                                        <Alert
                                            variant={submitStatus.type}
                                            onClose={() => setSubmitStatus({ type: null, text: '' })}
                                            dismissible
                                            className="mt-3"
                                        >
                                            {submitStatus.text}
                                        </Alert>
                                    )}
                                    {!(submitStatus.type === 'success') && (
                                        <Form onSubmit={handleSubmit}>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3 floating-label-group">
                                                        <Form.Control type="text" name="fullName" value={formData.fullName} onChange={handleChange} placeholder=" " required id="salesFullName" disabled={isSubmitting} />
                                                        <Form.Label htmlFor="salesFullName"><FontAwesomeIcon icon={faUser} className="me-2" />Full Name</Form.Label>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3 floating-label-group">
                                                        <Form.Control type="email" name="workEmail" value={formData.workEmail} onChange={handleChange} placeholder=" " required id="salesWorkEmail" disabled={isSubmitting} />
                                                        <Form.Label htmlFor="salesWorkEmail"><FontAwesomeIcon icon={faEnvelope} className="me-2" />Work Email</Form.Label>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="mb-3 floating-label-group">
                                                <Form.Control type="text" name="companyName" value={formData.companyName} onChange={handleChange} placeholder=" " required id="salesCompanyName" disabled={isSubmitting} />
                                                <Form.Label htmlFor="salesCompanyName"><FontAwesomeIcon icon={faBuilding} className="me-2" />Company Name</Form.Label>
                                            </Form.Group>

                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3 floating-label-group">
                                                        <Form.Control type="text" name="teamSize" value={formData.teamSize} onChange={handleChange} placeholder=" " id="salesTeamSize" disabled={isSubmitting} />
                                                        <Form.Label htmlFor="salesTeamSize"><FontAwesomeIcon icon={faUsers} className="me-2" />Est. Team Size / Clients</Form.Label>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3 floating-label-group">
                                                        <Form.Control type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder=" " id="salesPhone" disabled={isSubmitting} />
                                                        <Form.Label htmlFor="salesPhone"><FontAwesomeIcon icon={faPhone} className="me-2" />Phone (Optional)</Form.Label>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="mb-4 floating-label-group">
                                                <Form.Control as="textarea" rows={5} name="message" value={formData.message} onChange={handleChange} placeholder=" " required id="salesMessage" style={{ resize: 'none' }} disabled={isSubmitting} />
                                                <Form.Label htmlFor="salesMessage"><FontAwesomeIcon icon={faAlignLeft} className="me-2" />Your Needs & Questions</Form.Label>
                                            </Form.Group>

                                            <div className="mb-4">
                                                <Turnstile
                                                    sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"} // Fallback a test key
                                                    onVerify={(token) => setCaptchaToken(token)}
                                                    onError={() => setSubmitStatus({ type: 'danger', text: 'Security challenge failed. Please refresh and try again.' })}
                                                    onExpire={() => setCaptchaToken('')}
                                                    theme="light" // o 'dark'
                                                />
                                            </div>

                                            <div className="d-grid">
                                                <Button variant="primary" type="submit" size="lg" disabled={isSubmitting || !captchaToken} className="fw-bold py-3">
                                                    {isSubmitting ? (
                                                        <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />Sending Inquiry...</>
                                                    ) : (
                                                        <><FontAwesomeIcon icon={faPaperPlane} className="me-2" />Submit Inquiry</>
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={5} className="d-flex">
                            <Card className="contact-info-card bg-primary text-white shadow-lg border-0 w-100">
                                <Card.Body className="p-4 p-md-5 d-flex flex-column">
                                    <h3 className="fw-bold mb-4">Sales Department</h3>
                                    <p className="mb-4">
                                        Our sales team is ready to assist you with enterprise solutions, custom requirements, and any questions about our Business plan.
                                    </p>
                                    <div className="contact-detail mb-3">
                                        <FontAwesomeIcon icon={faEnvelope} size="lg" className="me-3" />
                                        <a href={`mailto:sales@${appName.toLowerCase().replace(/\s+/g, '')}.com`} className="text-white text-decoration-none">sales@{appName.toLowerCase().replace(/\s+/g, '')}.com</a>
                                    </div>
                                    <div className="contact-detail mb-4">
                                        <FontAwesomeIcon icon={faQuestionCircle} size="lg" className="me-3" />
                                        <span>Have a general question? Visit our <a href="/contact" className="text-white fw-bold text-decoration-underline">general contact page</a>.</span>
                                    </div>
                                    <div className="mt-auto">
                                        <h5 className="fw-semibold mb-3">Connect With {appName}</h5>
                                        <div className="social-icons-contact">
                                            <a href="https://linkedin.com/company/myfitmenu-ai" target="_blank" rel="noopener noreferrer" className="social-icon me-3" aria-label="LinkedIn">
                                                <FaLinkedin size={32} />
                                            </a>
                                            <a href="https://twitter.com/myfitmenu_ai" target="_blank" rel="noopener noreferrer" className="social-icon me-3" aria-label="Twitter">
                                                <FaTwitter size={32} />
                                            </a>
                                            <a href="https://instagram.com/myfitmenu_ai" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
                                                <FaInstagram size={32} />
                                            </a>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>
            <Footer />
        </div>
    );
};

export default ContactSalesPage;